import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { DatePicker } from '@components/core-ui/form-elements/DatePicker';
import { Input } from '@components/core-ui/form-elements/Input';
import CloseIcon from '@components/icons/Close.svg';
import { TDurationFilter } from 'app/types/TDurationFilter';
import { TJobsFilter } from 'app/types/TJobsFilter';
import { TUserFilter } from 'app/types/TUserFilter';
import { useState } from 'react';
import { ButtonsWrapper } from './styles/ButtonsWrapper';
import { Close } from './styles/Close';
import { FilterTitle } from './styles/FilterTitle';
import { SuggestionButtonsWrapper } from './styles/SuggestionButtonsWrapper';
import { UsersCreatedWrapper } from './styles/UsersCreatedWrapper';
import { Wrapper } from './styles/Wrapper';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TAppliedFilters } from './types/TAppliedFilters';
import { isMinutesInputInvalid } from './helpers/isMinutesInputInvalid';
import { isMinutesRangeInvalid } from './helpers/isMinutesRangeInvalid';
import { parseMinutesInput } from './helpers/parseMinutesInput';
import { RECORDING_SESSION_TYPES } from 'app/types/enums/recordingSessionTypes';

const firstDayOfCurrentWeek = () => {
  const dayOfWeek = new Date().getDay();
  const firstDayOfWeek = new Date();
  const diff = dayOfWeek >= 0 ? dayOfWeek : 6 - dayOfWeek;
  firstDayOfWeek.setDate(new Date().getDate() - diff);
  firstDayOfWeek.setHours(0, 0, 0, 0);
  return firstDayOfWeek;
};

const firstDayOfCurrentMonth = () => {
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDate() + 1);
  firstDayOfMonth.setHours(0, 0, 0, 0);
  return firstDayOfMonth;
};

const startOfCurrentDay = () => {
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  return currentDay;
};

const getSelectedDateFilterValue = (jobsFilter: TJobsFilter) => {
  let to = jobsFilter.to || 0;
  let from = jobsFilter.from || 0;
  const today = startOfCurrentDay().getTime();
  const thisWeekStart = firstDayOfCurrentWeek().getTime();
  const thisMonthStart = firstDayOfCurrentMonth().getTime();
  if (from) {
    from = parseInt(new Date(from).setHours(0, 0, 0, 0).toString(), 10);
  }
  if (to) {
    to = parseInt(new Date(to).setHours(0, 0, 0, 0).toString(), 10);
  }
  if (from === today && to === today) {
    return 'today';
  }
  if (from === thisWeekStart && to === today) {
    return 'week';
  }
  if (from === thisMonthStart && to === today) {
    return 'month';
  }
  return '';
};

type TProps = {
  userFilter?: TUserFilter;
  userFilterOnChange?: (arg: TUserFilter) => void;
  jobsFilter: TJobsFilter;
  jobsFilterOnChange?: (arg: TJobsFilter) => void;
  statusFilter: string[];
  statusFilterOnChange?: (arg: string[]) => void;
  onApplyFilters?: (filters: TAppliedFilters) => void;
  handleSubmit: () => void;
  handleClose: () => void;
  hideHeader?: boolean;
  asOneFilter?: boolean;
  setAsOneFilter?: (v: boolean) => void;
  postFilter?: boolean;
  setPostFilter?: (v: boolean) => void;
  meetingTypeFilter?: string[];
  durationFilter?: TDurationFilter;
  setDurationFilter?: (v: TDurationFilter) => void;
};

export const Filter = ({
  userFilter,
  userFilterOnChange,
  jobsFilter,
  jobsFilterOnChange,
  statusFilter,
  statusFilterOnChange,
  onApplyFilters,
  handleSubmit,
  handleClose,
  hideHeader = false,
  asOneFilter,
  setAsOneFilter,
  postFilter,
  setPostFilter,
  meetingTypeFilter,
  durationFilter,
  setDurationFilter,
}: TProps) => {
  const [tempDateFilterSelected, setTempDateFilterSelected] = useState(getSelectedDateFilterValue(jobsFilter));
  const [tempJobsFilter, setTempJobsFilter] = useState(jobsFilter);
  const [tempUserFilter, setTempUserFilter] = useState(userFilter);
  const [tempStatusFilter, setTempStatusFilter] = useState(statusFilter);
  const [tempAsOneFilter, setTempAsOneFilter] = useState(asOneFilter ?? false);
  const [tempPostFilter, setTempPostFilter] = useState(postFilter ?? false);
  const [tempMeetingTypeFilter, setTempMeetingTypeFilter] = useState(meetingTypeFilter ?? []);
  const [tempDurationFrom, setTempDurationFrom] = useState(
    durationFilter?.fromMinutes === undefined ? '' : String(durationFilter.fromMinutes),
  );
  const [tempDurationTo, setTempDurationTo] = useState(
    durationFilter?.toMinutes === undefined ? '' : String(durationFilter.toMinutes),
  );

  const durationFromInvalid = isMinutesInputInvalid(tempDurationFrom);
  const durationToInvalid = isMinutesInputInvalid(tempDurationTo);
  const durationRangeInvalid = isMinutesRangeInvalid(tempDurationFrom, tempDurationTo);
  const durationInvalid = durationFromInvalid || durationToInvalid || durationRangeInvalid;

  const applyFilters = (filters: TAppliedFilters) => {
    if (onApplyFilters) {
      onApplyFilters(filters);
      return;
    }
    if (userFilterOnChange) {
      userFilterOnChange(tempUserFilter ?? {});
    }
    if (setAsOneFilter) {
      setAsOneFilter(filters.asOneFilter ?? false);
    }
    if (setPostFilter) {
      setPostFilter(filters.postFilter ?? false);
    }
    if (setDurationFilter) {
      setDurationFilter(filters.durationFilter ?? {});
    }
    if (jobsFilterOnChange) {
      jobsFilterOnChange(filters.jobsFilter);
    }
    if (statusFilterOnChange) {
      statusFilterOnChange(filters.statusFilter);
    }
  };

  const tempSubmit = () => {
    if (durationInvalid) {
      return;
    }
    const nextMeetingTypeFilter = tempAsOneFilter ? tempMeetingTypeFilter : [];
    applyFilters({
      jobsFilter: tempJobsFilter,
      statusFilter: tempStatusFilter,
      asOneFilter: tempAsOneFilter,
      postFilter: tempPostFilter,
      meetingTypeFilter: nextMeetingTypeFilter,
      durationFilter: {
        fromMinutes: parseMinutesInput(tempDurationFrom),
        toMinutes: parseMinutesInput(tempDurationTo),
      },
    });
    handleSubmit();
    handleClose();
  };
  const tempClose = () => {
    handleClose();
  };

  const statuses = [
    { value: 'JOB_STATUS_DRAFT', label: 'Draft' },
    { value: 'JOB_STATUS_ERROR', label: 'Error' },
    { value: 'JOB_STATUS_SCHEDULED', label: 'Scheduled' },
    { value: 'JOB_STATUS_PROCESSING', label: 'Processing' },
    { value: 'JOB_STATUS_READY_FOR_TRANSCRIBING', label: 'Ready for Editing' },
    { value: 'JOB_STATUS_TRANSCRIBING', label: 'Editing' },
    { value: 'JOB_STATUS_READY_FOR_PROOFREADING', label: 'Ready for Proofreading' },
    { value: 'JOB_STATUS_PROOFING', label: 'Proofreading' },
    { value: 'JOB_STATUS_READY_FOR_FINAL', label: 'Ready for final' },
    { value: 'JOB_STATUS_FINALIZING', label: 'Finalizing' },
    { value: 'JOB_STATUS_COMPLETE', label: 'Complete' },
    { value: 'JOB_STATUS_LOCKED', label: 'Locked' },
  ];

  const statusCheckboxes = statuses.map((status) => (
    <div className="checkbox-wrapper" key={status.value}>
      <CheckBox
        name={status.value}
        onChange={() => {
          let newFilter = [...tempStatusFilter];
          if (tempStatusFilter.indexOf(status.value) > -1) {
            newFilter = newFilter.filter((v: string) => v !== status.value);
          } else {
            newFilter.push(status.value);
          }
          setTempStatusFilter(newFilter);
        }}
        checked={tempStatusFilter.indexOf(status.value) > -1}
      />
      <label>{status.label}</label>
    </div>
  ));

  return (
    <Wrapper $compact={hideHeader}>
      {hideHeader ? null : (
        <>
          <FilterTitle>Filters</FilterTitle>
          <Close onClick={tempClose}>
            <CloseIcon />
          </Close>
        </>
      )}

      <div className="label">Status</div>
      {hideHeader ? <div className="status-list">{statusCheckboxes}</div> : statusCheckboxes}
      {asOneFilter !== undefined || postFilter !== undefined ? (
        <>
          <div className="label">Job type</div>
          <div className="job-type-wrapper">
            {asOneFilter !== undefined ? (
              <div className="checkbox-wrapper">
                <CheckBox
                  name="asone-filter"
                  onChange={() => {
                    const nextAsOneFilter = !tempAsOneFilter;
                    setTempAsOneFilter(nextAsOneFilter);
                    if (nextAsOneFilter) {
                      setTempPostFilter(false);
                    } else {
                      setTempMeetingTypeFilter([]);
                    }
                  }}
                  checked={tempAsOneFilter}
                />
                <label>ASOne</label>
              </div>
            ) : null}
            {postFilter !== undefined ? (
              <div className="checkbox-wrapper">
                <CheckBox
                  name="post-filter"
                  onChange={() => {
                    const nextPostFilter = !tempPostFilter;
                    setTempPostFilter(nextPostFilter);
                    if (nextPostFilter) {
                      setTempAsOneFilter(false);
                      setTempMeetingTypeFilter([]);
                    }
                  }}
                  checked={tempPostFilter}
                />
                <label>POST</label>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {asOneFilter !== undefined && tempAsOneFilter ? (
        <>
          <div className="label">Session type</div>
          {RECORDING_SESSION_TYPES.map((sessionType) => (
            <div className="checkbox-wrapper" key={sessionType.value}>
              <CheckBox
                name={sessionType.value}
                onChange={() => {
                  let newFilter = [...tempMeetingTypeFilter];
                  if (tempMeetingTypeFilter.indexOf(sessionType.value) > -1) {
                    newFilter = newFilter.filter((v: string) => v !== sessionType.value);
                  } else {
                    newFilter.push(sessionType.value);
                  }
                  setTempMeetingTypeFilter(newFilter);
                }}
                checked={tempMeetingTypeFilter.indexOf(sessionType.value) > -1}
              />
              <label>{sessionType.label}</label>
            </div>
          ))}
        </>
      ) : null}

      {durationFilter !== undefined ? (
        <>
          <div className="label">Duration, minutes</div>
          <div className="duration-wrapper">
            <Input
              label="From"
              name="duration-from"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              error={durationFromInvalid || durationRangeInvalid}
              value={tempDurationFrom}
              onChange={(event) => setTempDurationFrom(event.target.value)}
            />
            <Input
              label="To"
              name="duration-to"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              error={durationToInvalid || durationRangeInvalid}
              value={tempDurationTo}
              onChange={(event) => setTempDurationTo(event.target.value)}
            />
          </div>
          {durationFromInvalid || durationToInvalid ? (
            <div className="duration-error">Enter whole minutes, 0 or more</div>
          ) : null}
          {durationRangeInvalid ? <div className="duration-error">From must not be greater than To</div> : null}
        </>
      ) : null}

      <div className="label">Jobs submitted</div>
      <div className="datepicker-wrapper">
        <DatePicker
          label="From"
          minDate={new Date(0)}
          maxDate={tempJobsFilter.to || new Date()}
          value={tempJobsFilter.from}
          onChange={(date: Date | null) =>
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = date ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <div className="datepicker-wrapper">
        <DatePicker
          label="To"
          minDate={tempJobsFilter.from}
          maxDate={new Date()}
          value={tempJobsFilter.to}
          onChange={(date: Date | null) =>
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              let newDate = null;
              if (date) {
                const dateObj = new Date(date);
                dateObj.setHours(23);
                dateObj.setMinutes(59);
                newDate = dateObj;
              }
              newVal.to = newDate ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <SuggestionButtonsWrapper>
        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'today'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = startOfCurrentDay();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('today');
          }}
        >
          Today
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'week'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = firstDayOfCurrentWeek();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('week');
          }}
        >
          This week
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'month'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = firstDayOfCurrentMonth();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('month');
          }}
        >
          This month
        </OutlinedButton>
      </SuggestionButtonsWrapper>

      {userFilter && userFilterOnChange ? (
        <UsersCreatedWrapper>
          <div className="label">Users created</div>
          <div className="datepicker-wrapper">
            <DatePicker
              minDate={new Date(0)}
              maxDate={tempUserFilter?.userTo ?? new Date()}
              value={tempUserFilter?.userFrom}
              onChange={(date: Date | null) => setTempUserFilter({ ...tempUserFilter, userFrom: date })}
              label="From"
            />
          </div>
          <div className="datepicker-wrapper">
            <DatePicker
              minDate={tempUserFilter?.userFrom ?? undefined}
              maxDate={new Date()}
              value={tempUserFilter?.userTo}
              onChange={(date: Date | null) =>
                setTempUserFilter((v) => {
                  const newVal: TUserFilter = Object.assign({}, v);
                  let newDate = null;
                  if (date) {
                    const dateObj = new Date(date);
                    dateObj.setHours(23);
                    dateObj.setMinutes(59);
                    newDate = dateObj;
                  }
                  newVal.userTo = newDate ?? undefined;
                  return newVal;
                })
              }
              label="To"
            />
          </div>
        </UsersCreatedWrapper>
      ) : null}

      <ButtonsWrapper>
        <OutlinedButton
          onClick={() => {
            setTempJobsFilter({ from: undefined, to: undefined });
            setTempDateFilterSelected('');
            setTempStatusFilter([]);
            setTempAsOneFilter(false);
            setTempPostFilter(false);
            setTempMeetingTypeFilter([]);
            setTempDurationFrom('');
            setTempDurationTo('');
            if (userFilterOnChange) {
              setTempUserFilter({ userFrom: undefined, userTo: undefined });
              userFilterOnChange({ userFrom: undefined, userTo: undefined });
            }
            applyFilters({
              jobsFilter: { from: undefined, to: undefined },
              statusFilter: [],
              asOneFilter: false,
              postFilter: false,
              meetingTypeFilter: [],
              durationFilter: {},
            });
            handleSubmit();
            handleClose();
          }}
        >
          Reset
        </OutlinedButton>
        <Button onClick={tempSubmit} disabled={durationInvalid}>
          Apply
        </Button>
      </ButtonsWrapper>
    </Wrapper>
  );
};
